import { useCallback, useEffect, useState } from 'react';
import { KeyRound, Plus, RefreshCw, Trash2, Copy, Check, AlertTriangle, ShieldAlert } from 'lucide-react';
import { SupervisorApiTokenView } from '@bloom/shared';
import { apiClient } from '../lib/api';
import { toast } from '../lib/toast';

/**
 * A partner's own integration credentials.
 *
 * Reachable only when an admin has turned on API access for the account — the same
 * flag that makes a supervisor a partner at all.
 *
 * Two things this page is careful about:
 *  - A new token's plaintext exists once, in the response that created it. It is
 *    held in component state and shown until dismissed; there is no endpoint that
 *    can return it again, so the copy affordance has to be obvious.
 *  - Revoke is immediate and rotate is not. They are deliberately worded and
 *    styled differently, because picking the wrong one breaks a live integration.
 */

const formatWhen = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const relativeHours = (iso?: string) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const hours = Math.round(ms / 3_600_000);
  if (hours < 1) return 'under an hour';
  return `${hours} hour${hours === 1 ? '' : 's'}`;
};

const STATE_STYLE: Record<SupervisorApiTokenView['state'], { bg: string; text: string; label: string }> = {
  active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
  expiring: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Rotated' },
  expired: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Expired' },
  revoked: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Revoked' }
};

function SecretBanner({ apiKey, note, onDismiss }: { apiKey: string; note?: string; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — select the token and copy it manually');
    }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <div className="flex items-start gap-2">
        <ShieldAlert className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-blue-900">Copy this token now</div>
          <p className="text-sm text-blue-800 mt-0.5">
            This is the only time it can be shown. We store a hash, so it cannot be
            recovered later — if you lose it, rotate the token.
            {note ? ` ${note}` : ''}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 min-w-0 truncate bg-white border border-blue-200 rounded px-3 py-2 text-xs font-mono text-gray-900">
              {apiKey}
            </code>
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
        <button onClick={onDismiss} className="text-blue-400 hover:text-blue-600 text-sm">
          Dismiss
        </button>
      </div>
    </div>
  );
}

export default function ApiTokens() {
  const [tokens, setTokens] = useState<SupervisorApiTokenView[]>([]);
  const [hasLegacy, setHasLegacy] = useState(false);
  const [graceHours, setGraceHours] = useState(24);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState('');
  const [secret, setSecret] = useState<{ apiKey: string; note?: string } | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await apiClient.listMyApiTokens();
    if (res.success && res.data) {
      setTokens(res.data.tokens);
      setHasLegacy(res.data.hasLegacyCredential);
      setGraceHours(res.data.graceHours);
    } else {
      toast.error(res.error || 'Failed to load your API tokens');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    const res = await apiClient.createMyApiToken(label.trim() || undefined);
    setCreating(false);
    if (res.success && res.data) {
      setSecret({ apiKey: res.data.apiKey });
      setLabel('');
      await load();
    } else {
      toast.error(res.error || 'Failed to create the token');
    }
  };

  const rotate = async (token: SupervisorApiTokenView) => {
    setBusyId(token.id);
    const res = await apiClient.rotateMyApiToken(token.id);
    setBusyId(null);
    if (res.success && res.data) {
      setSecret({
        apiKey: res.data.apiKey,
        note: `The previous token keeps working until ${formatWhen(res.data.previousExpiresAt)}.`
      });
      await load();
    } else {
      toast.error(res.error || 'Failed to rotate the token');
    }
  };

  const rotateLegacy = async () => {
    setBusyId('legacy');
    const res = await apiClient.rotateMyLegacyApiKey();
    setBusyId(null);
    if (res.success && res.data) {
      setSecret({
        apiKey: res.data.apiKey,
        note: `Your original key keeps working until ${formatWhen(res.data.previousExpiresAt)}.`
      });
      await load();
    } else {
      toast.error(res.error || 'Failed to rotate your original key');
    }
  };

  const revoke = async (tokenId: string) => {
    setBusyId(tokenId);
    const res = await apiClient.revokeMyApiToken(tokenId);
    setBusyId(null);
    setConfirmRevoke(null);
    if (res.success) {
      toast.success('Token revoked — it stops working immediately');
      await load();
    } else {
      toast.error(res.error || 'Failed to revoke the token');
    }
  };

  const live = tokens.filter(t => t.state === 'active' || t.state === 'expiring');

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="h-6 w-6 text-gray-500" />
        <h1 className="text-2xl font-semibold text-gray-900">API Tokens</h1>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Bearer tokens for the Integration API. Rotating issues a replacement and
        leaves the old token working for {graceHours} hours so you can redeploy
        without downtime. Revoking takes effect immediately.
      </p>

      {secret && (
        <SecretBanner apiKey={secret.apiKey} note={secret.note} onDismiss={() => setSecret(null)} />
      )}

      {hasLegacy && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-amber-900">You have an original key not listed here</div>
              <p className="text-sm text-amber-800 mt-0.5">
                It was issued before this page existed and still works. Rotate it to
                replace it with a listed token — the original keeps working for{' '}
                {graceHours} hours after you do.
              </p>
              <button
                type="button"
                onClick={rotateLegacy}
                disabled={busyId === 'legacy'}
                className="mt-3 inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${busyId === 'legacy' ? 'animate-spin' : ''}`} />
                Rotate original key
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Create a token</label>
        <div className="flex gap-2">
          <input
            value={label}
            onChange={e => setLabel(e.target.value)}
            maxLength={60}
            placeholder="Label (optional) — e.g. production, staging"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="button"
            onClick={create}
            disabled={creating}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            <Plus className="h-4 w-4 mr-1" />
            {creating ? 'Creating…' : 'Create'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          A label only helps you tell them apart — it is not sent with requests.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : tokens.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-300 rounded-lg">
          <KeyRound className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">No tokens yet.</p>
          <p className="text-xs text-gray-500 mt-1">
            Create one above to start calling the Integration API.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
          {tokens.map(t => {
            const style = STATE_STYLE[t.state];
            const graceLeft = t.state === 'expiring' ? relativeHours(t.expiresAt) : null;
            const actionable = t.state === 'active' || t.state === 'expiring';
            return (
              <div key={t.id} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 truncate">
                        {t.label || 'Untitled token'}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
                        {style.label}
                      </span>
                      {t.hint && (
                        <code className="text-xs font-mono text-gray-500">…{t.hint}</code>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                      <div>Created {formatWhen(t.createdAt)}</div>
                      <div>
                        {t.lastUsedAt ? `Last used ${formatWhen(t.lastUsedAt)}` : 'Never used'}
                      </div>
                      {graceLeft && (
                        <div className="text-amber-700">
                          Superseded — stops working in {graceLeft} ({formatWhen(t.expiresAt)})
                        </div>
                      )}
                      {t.state === 'revoked' && <div>Revoked {formatWhen(t.revokedAt)}</div>}
                    </div>
                  </div>

                  {actionable && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => rotate(t)}
                        disabled={busyId === t.id}
                        title={`Issue a replacement; this token keeps working for ${graceHours}h`}
                        className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      >
                        <RefreshCw className={`h-4 w-4 mr-1 ${busyId === t.id ? 'animate-spin' : ''}`} />
                        Rotate
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmRevoke(t.id)}
                        disabled={busyId === t.id}
                        title="Stop this token working immediately"
                        className="inline-flex items-center px-3 py-1.5 text-sm text-red-700 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Revoke
                      </button>
                    </div>
                  )}
                </div>

                {confirmRevoke === t.id && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3">
                    <div className="text-sm text-red-900 font-medium">
                      Revoke {t.label || 'this token'} now?
                    </div>
                    <p className="text-xs text-red-800 mt-0.5">
                      Anything using it starts failing on its next request. There is no
                      grace period — if you want one, rotate instead.
                      {live.length === 1 && ' This is your only live token.'}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => revoke(t.id)}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                      >
                        Revoke now
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmRevoke(null)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Keep it
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
